import { useState } from "react";
import { Mail, MapPin, Phone, MessageCircle, Globe, CheckCircle2, Loader2, Instagram, Facebook } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, isEmail } from "@/components/forms/FormBits";
import { submitContact } from "@/lib/api";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { site, whatsappHref } from "@/config/site";
import { IMG } from "@/data/content";

const empty = { name: "", property_name: "", phone: "", email: "", city: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const wa = whatsappHref();

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!isEmail(form.email)) e.email = "Please enter a valid email address.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await submitContact(form);
      trackEvent(EVENTS.CONTACT_SUBMITTED);
      setStatus("success");
      setForm(empty);
    } catch {
      setStatus("error");
    }
  };

  const contactItems = [
    { icon: Mail, label: "Email", value: site.email, href: `mailto:${site.email}`, testId: "contact-email" },
    site.phone && { icon: Phone, label: "Phone", value: site.phone, href: `tel:${site.phone.replace(/\s+/g, "")}`, testId: "contact-phone" },
    wa && { icon: MessageCircle, label: "WhatsApp", value: "Message us", href: wa, testId: "contact-whatsapp" },
    { icon: Globe, label: "Website", value: site.domain, href: site.url, testId: "contact-website" },
    { icon: MapPin, label: "Location", value: site.location, href: null, testId: "contact-location" },
  ].filter(Boolean);

  return (
    <>
      <Seo title="Contact HM Services | Let's Talk About Your Property" description="Get in touch with HM Services. Email info.hassanalihms@gmail.com to discuss digital growth for your hotel, guest house, resort or villa." path="/contact" image={IMG.lobby} />
      <PageHero eyebrow="Contact" title="Let's Talk About Your Property" subtitle="Tell us about your property and goals. We'll get back to you with practical next steps." image={IMG.apartment} />

      <section className="bg-white py-24 lg:py-32" data-testid="contact-page">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr]">
          {/* Info */}
          <Reveal variant="left">
            <h2 className="font-serif text-3xl font-bold text-navy">HM Services</h2>
            <p className="mt-2 text-lg text-ocean">{site.founder.name}</p>
            <p className="text-sm text-slate-500">{site.founder.role}</p>

            <ul className="mt-10 space-y-5">
              {contactItems.map((c) => (
                <li key={c.label} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-mist text-ocean">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="break-all font-medium text-navy transition-colors hover:text-ocean" data-testid={c.testId}>{c.value}</a>
                    ) : (
                      <p className="font-medium text-navy" data-testid={c.testId}>{c.value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
                        <div className="mt-8">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                Follow HM Services
              </p>

              <div className="mt-4 flex flex-wrap gap-3">
                {site.social.instagram && (
                  <a
                    href={site.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist text-ocean transition-colors hover:bg-ocean hover:text-white"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}

                {site.social.facebook && (
                  <a
                    href={site.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist text-ocean transition-colors hover:bg-ocean hover:text-white"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}

                {site.social.tiktok && (
                  <a
                    href={site.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                    className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist text-ocean transition-colors hover:bg-ocean hover:text-white"
                  >
                    <svg
  className="h-5 w-5"
  viewBox="0 0 24 24"
  fill="currentColor"
  aria-hidden="true"
>
  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-3.95V2h-3.54v13.36a2.95 2.95 0 1 1-2.95-2.95c.31 0 .61.05.89.14V9.02a6.5 6.5 0 0 0-.89-.06A6.41 6.41 0 1 0 15.82 15V8.41a8.36 8.36 0 0 0 4.9 1.58V6.69h-1.13z" />
</svg>
                  </a>
                )}
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal>
            <div className="rounded-3xl border border-line bg-white p-8 shadow-float-lg sm:p-10">
              {status === "success" ? (
                <div className="flex flex-col items-center py-16 text-center" data-testid="contact-success">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-ocean/10"><CheckCircle2 className="h-9 w-9 text-ocean" /></div>
                  <h3 className="mt-6 font-serif text-2xl text-navy">Thank you.</h3>
                  <p className="mt-3 max-w-sm text-sm text-slate-600">Your inquiry has been submitted successfully. HM Services will contact you shortly.</p>
                  <Button onClick={() => setStatus("idle")} className="mt-8 rounded-full bg-navy px-8 text-white hover:bg-navy-800" data-testid="contact-reset">Send Another Message</Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <TextField id="c-name" label="Name" required value={form.name} onChange={set("name")} error={errors.name} placeholder="Your name" data-testid="contact-name" />
                    <TextField id="c-property" label="Property Name" value={form.property_name} onChange={set("property_name")} placeholder="Optional" data-testid="contact-property" />
                    <TextField id="c-phone" label="Phone" value={form.phone} onChange={set("phone")} placeholder="Optional" data-testid="contact-phone-input" />
                    <TextField id="c-email" label="Email" required type="email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@email.com" data-testid="contact-email-input" />
                  </div>
                  <TextField id="c-city" label="City" value={form.city} onChange={set("city")} placeholder="Optional" data-testid="contact-city" />
                  <TextArea id="c-message" label="Message" required rows={5} value={form.message} onChange={set("message")} error={errors.message} placeholder="How can we help your property?" data-testid="contact-message" />
                  {status === "error" && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="contact-error">Something went wrong. Please try again.</p>}
                  <Button type="submit" disabled={status === "loading"} className="w-full rounded-full bg-ocean py-6 text-base text-white hover:bg-royal" data-testid="contact-submit">
                    {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : "Send Inquiry"}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
